/**
 * Press-and-hold to repeat (UI-007, owner, 2026-09-25): the aim nudge buttons step
 * once on a press, then keep stepping while held, faster the longer they are held. A
 * tenth of a degree a tap is right for the last touch of aim and hopeless for turning the
 * cue a long way.
 *
 * The timing is here, away from the DOM, behind an injectable clock: rAF and timers are
 * not something to judge by eye (L15).
 */

export interface Timers {
  setTimeout(callback: () => void, ms: number): number;
  clearTimeout(handle: number): void;
}

/** Held this long before repeating starts, so a tap is only ever one step. */
export const HOLD_DELAY_MS = 350;
/** Step interval once repeating. */
export const REPEAT_MS = 90;
/** After this many repeats, speed up. */
export const FAST_AFTER = 6;
export const FAST_REPEAT_MS = 25;
/**
 * And again (UI-014): a tenth of a degree a step is 2.5 times finer than it was, so the
 * gears come sooner and a third is added. A tap is still one step; held for two seconds
 * the cue turns about 11 degrees (it was about 9), and for three, about 21 (was 16).
 */
export const FASTER_AFTER = 20;
export const FASTER_REPEAT_MS = 10;

export interface HoldRepeat {
  /** Press: one step now, then repeat until `stop`. */
  start(): void;
  stop(): void;
}

export function createHoldRepeat(step: () => void, timers: Timers): HoldRepeat {
  let handle: number | undefined;
  let repeats = 0;

  const tick = (): void => {
    step();
    repeats++;
    handle = timers.setTimeout(
      tick,
      repeats >= FASTER_AFTER
        ? FASTER_REPEAT_MS
        : repeats >= FAST_AFTER
          ? FAST_REPEAT_MS
          : REPEAT_MS,
    );
  };

  const stop = (): void => {
    if (handle !== undefined) timers.clearTimeout(handle);
    handle = undefined;
  };

  return {
    start() {
      stop();
      repeats = 0;
      step();
      handle = timers.setTimeout(tick, HOLD_DELAY_MS);
    },
    stop,
  };
}
